import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileText, X, Loader2, Printer, CheckCircle2, IndianRupee,
} from "lucide-react";

const PAPER = ["A4", "A5", "Letter", "Legal", "A3"];

export default function UploadPrint() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    copies: 1,
    color: false,
    duplex: false,
    paper_size: "A4",
    orientation: "portrait",
    page_range: "",
    printer_id: "",
  });
  const [drag, setDrag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      const [p, s] = await Promise.all([api.get("/printers"), api.get("/settings")]);
      setPrinters(p.data);
      setSettings(s.data);
      const def = p.data.find((x) => x.is_default) || p.data[0];
      if (def) setForm((f) => ({ ...f, printer_id: def.id }));
    })();
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }, []);

  const handleFile = (f) => {
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are supported");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15MB)");
      return;
    }
    setFile(f);
  };

  const estimate = () => {
    if (!settings) return { subtotal: 0, gst: 0, total: 0 };
    const per = form.color ? settings.price_color : settings.price_bw;
    const duplex = form.duplex ? settings.price_duplex_extra : 0;
    // We don't know page count client-side without parsing PDF, so assume 1 page * copies as base preview
    const pages = 1; // real pages calculated backend
    const subtotal = (per + duplex) * pages * form.copies;
    const gst = (subtotal * (settings.gst_pct || 0)) / 100;
    return { subtotal: subtotal.toFixed(2), gst: gst.toFixed(2), total: (subtotal + gst).toFixed(2) };
  };

  const submit = async () => {
    if (!file) return toast.error("Please select a PDF file");
    setSubmitting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("copies", String(form.copies));
      fd.append("color", String(form.color));
      fd.append("duplex", String(form.duplex));
      fd.append("paper_size", form.paper_size);
      fd.append("orientation", form.orientation);
      if (form.page_range) fd.append("page_range", form.page_range);
      if (form.printer_id) fd.append("printer_id", form.printer_id);

      const { data } = await api.post("/orders", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Print sent to ${data.printer_name || "queue"}!`);
      await refreshMe();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const est = estimate();
  const money = (v) => "₹" + Number(v).toFixed(2);

  if (result) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="print-success">
        <Card className="p-8 border-border bg-card text-center">
          <div className="h-16 w-16 mx-auto rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center">
            <CheckCircle2 size={36} />
          </div>
          <h2 className="mt-4 text-2xl font-display font-semibold">Print completed!</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Silent auto-print delivered to <span className="font-medium text-foreground">{result.printer_name || "your default printer"}</span>
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4 text-left">
            <div className="p-3 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Pages</p>
              <p className="text-xl font-display font-semibold tabular-nums">{result.total_pages}</p>
            </div>
            <div className="p-3 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Copies</p>
              <p className="text-xl font-display font-semibold tabular-nums">{result.copies}</p>
            </div>
            <div className="p-3 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Charged</p>
              <p className="text-xl font-display font-semibold tabular-nums text-brand-green">₹{result.cost}</p>
            </div>
          </div>
          <div className="mt-6 flex gap-2 justify-center">
            <Button variant="outline" onClick={() => { setFile(null); setResult(null); }} data-testid="print-new-btn">
              Print another
            </Button>
            <Button className="bg-primary hover:bg-brand-blue-hover" onClick={() => nav("/orders")} data-testid="print-view-orders-btn">
              View all orders
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="upload-print-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Print</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">New print job</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Upload a PDF, choose settings, and MaaPrint will silently print it to your default printer.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Upload area */}
        <div className="lg:col-span-3 space-y-6">
          <Card className="p-6 border-border bg-card">
            {!file ? (
              <label
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                className={`block cursor-pointer rounded-xl border-2 border-dashed transition-all p-12 text-center
                  ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`}
                data-testid="upload-dropzone"
              >
                <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                  <Upload size={24} />
                </div>
                <p className="mt-4 font-display text-lg font-medium">Drop PDF here or click to upload</p>
                <p className="text-xs text-muted-foreground mt-1">Max 15 MB · PDF only</p>
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                  data-testid="upload-file-input"
                />
              </label>
            ) : (
              <div className="flex items-center gap-4 p-4 rounded-xl border border-border bg-background">
                <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <FileText size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" data-testid="upload-filename">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB · PDF</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setFile(null)} data-testid="upload-clear-btn">
                  <X size={16} />
                </Button>
              </div>
            )}
          </Card>

          <Card className="p-6 border-border bg-card">
            <h3 className="font-display font-semibold mb-4">Print settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Copies</Label>
                <Input
                  type="number" min={1} max={100}
                  value={form.copies}
                  onChange={(e) => setForm((f) => ({ ...f, copies: Math.max(1, parseInt(e.target.value) || 1) }))}
                  className="mt-1.5"
                  data-testid="print-copies-input"
                />
              </div>
              <div>
                <Label>Paper size</Label>
                <Select value={form.paper_size} onValueChange={(v) => setForm((f) => ({ ...f, paper_size: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="print-paper-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAPER.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Orientation</Label>
                <Select value={form.orientation} onValueChange={(v) => setForm((f) => ({ ...f, orientation: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="print-orientation-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pages (e.g. 1-3,5) — leave blank for all</Label>
                <Input
                  placeholder="all"
                  value={form.page_range}
                  onChange={(e) => setForm((f) => ({ ...f, page_range: e.target.value }))}
                  className="mt-1.5"
                  data-testid="print-page-range-input"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border col-span-2 sm:col-span-1">
                <div>
                  <Label>Color</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {form.color ? `Color • ₹${settings?.price_color}/page` : `B&W • ₹${settings?.price_bw}/page`}
                  </p>
                </div>
                <Switch
                  checked={form.color}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, color: v }))}
                  data-testid="print-color-switch"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border col-span-2 sm:col-span-1">
                <div>
                  <Label>Duplex</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {form.duplex ? `+₹${settings?.price_duplex_extra}/page` : "Single-sided"}
                  </p>
                </div>
                <Switch
                  checked={form.duplex}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, duplex: v }))}
                  data-testid="print-duplex-switch"
                />
              </div>
              <div className="col-span-2">
                <Label>Printer</Label>
                <Select value={form.printer_id} onValueChange={(v) => setForm((f) => ({ ...f, printer_id: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="print-printer-select">
                    <SelectValue placeholder={printers.length ? "Select a printer" : "No printers configured"} />
                  </SelectTrigger>
                  <SelectContent>
                    {printers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} · {p.status === "online" ? "Online" : "Offline"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {printers.length === 0 && user.role === "retailer" && (
                  <p className="text-xs text-amber-600 mt-2">
                    No printers yet. <button onClick={() => nav("/printers")} className="underline">Add one</button> to enable auto-print.
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          <Card className="p-6 border-border bg-card sticky top-24">
            <h3 className="font-display font-semibold flex items-center gap-2">
              <Printer size={18} className="text-primary" /> Auto-print summary
            </h3>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Mode</span>
                <span className="font-medium">{form.color ? "Color" : "Black & White"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duplex</span>
                <span className="font-medium">{form.duplex ? "Both sides" : "One side"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Paper</span>
                <span className="font-medium">{form.paper_size} · {form.orientation}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Copies</span>
                <span className="font-medium tabular-nums">{form.copies}</span>
              </div>
              <div className="border-t border-border my-4"></div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Est. per page (from)</span>
                <span className="font-medium tabular-nums">
                  ₹{(form.color ? settings?.price_color : settings?.price_bw) || 0}
                  {form.duplex ? ` + ₹${settings?.price_duplex_extra}` : ""}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">GST</span>
                <span className="font-medium tabular-nums">{settings?.gst_pct || 0}%</span>
              </div>
              <div className="p-3 rounded-lg bg-secondary/50 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Your wallet</span>
                  <span className="font-display text-lg font-semibold tabular-nums" data-testid="upload-wallet-balance">
                    ₹{Number(user.wallet_balance || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <Button
              onClick={submit}
              disabled={!file || submitting || !form.printer_id}
              className="mt-6 w-full h-11 bg-primary hover:bg-brand-blue-hover"
              data-testid="print-submit-btn"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin mr-2" /> Printing…</>
              ) : (
                <><IndianRupee size={16} className="mr-1" /> Auto-print now</>
              )}
            </Button>
            <p className="text-[11px] text-center text-muted-foreground mt-2">
              Silent background print · Wallet auto-deducted
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
